import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, IconButton, InputAdornment, LinearProgress, TextField, Tooltip } from '@mui/material';
import { ArrowBackIosNew, Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { get, find } from 'lodash';
import moment from 'moment/moment';

import { getSftpJobDetail, listSftpJobShipments } from '../../repositories/HttpRepository';
import { useStyles } from './styles';
import AxlTableContainer from '../../components/AxlTableContainer';
import { getStatusColor } from '../../constants/common';
import { useRecoilValue } from 'recoil';
import { selectorClientsActive } from '../../atoms/clients';
import { getFileName } from '../../utils/csvUtil';

function SftpJobDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [jobDetail, setJobDetail] = useState({});
  const [shipmentsResult, setShipmentsResult] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [keyword, setKeyword] = useState('');
  const activeClients = useRecoilValue(selectorClientsActive);
  const { classes } = useStyles();
  const DEFAULT_FILTERS = {
    q: '',
    job_ids: [id],
    statuses: null,
    page: 1,
    size: 20,
  };
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  useEffect(() => {
    setLoading(true);
    getSftpJobDetail(id).then((res) => {
      if (res.ok) {
        setJobDetail(res.data);
      } else {
        setJobDetail({});
      }
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    setFetching(true);
    const params = { ...filters, page: filters.page - 1 };
    listSftpJobShipments(params).then((res) => {
      if (res.ok) {
        setShipmentsResult(res.data);
      } else {
        setShipmentsResult({});
      }
      setFetching(false);
    });
  }, [filters]);

  const updateFilter = (name, value) => {
    setFilters({ ...filters, [name]: value });
  };

  const updateFilters = (updated) => {
    setFilters({ ...filters, ...updated });
  };

  const clearSearch = () => {
    setKeyword('');
    if (!!filters?.q) {
      updateFilters({ q: '', page: DEFAULT_FILTERS.page });
    }
  };

  const onPageChange = (page) => {
    updateFilter('page', page);
  };

  const goBack = () => {
    navigate(-1);
    // navigate(`/sftp-jobs`);
  };

  const renderShipmentLink = (id) => {
    return (
      id && (
        <a href={`${process.env.REACT_APP_DISPATCH_URL}/shipments/${id}`} target="_blank">
          {id}
        </a>
      )
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  const fields = [
    {
      fieldName: 'client_id',
      label: 'Client',
      isDisableSort: true,
      renderer: (v) => get(find(activeClients, ['client_id', v]), 'name', v),
    },
    { fieldName: 'internal_id', label: 'Internal ID', isDisableSort: true },
    { fieldName: 'tracking_code', label: 'Tracking Code', isDisableSort: true },
    {
      fieldName: 'shipment_id',
      label: 'Shipment ID',
      isDisableSort: true,
      renderer: (v) => renderShipmentLink(v),
    },
    {
      fieldName: 'status',
      label: 'Status',
      isDisableSort: true,
      renderer: (v) => <Box sx={{ color: getStatusColor(v), fontWeight: 700 }}>{v}</Box>,
    },
    { fieldName: 'error', label: 'Error', isDisableSort: true },
    { fieldName: 'lane', label: 'Lane', isDisableSort: true },
    { fieldName: 'lane_code', label: 'Lane Code', isDisableSort: true },
    { fieldName: 'customer.name', label: 'Customer Name', isDisableSort: true },
    {
      fieldName: 'customer.phone_number',
      label: 'Customer Phone',
      isDisableSort: true,
    },
    {
      fieldName: 'customer.email',
      label: 'Customer Email',
      isDisableSort: true,
    },
    {
      fieldName: 'dropoff_earliest_ts',
      label: 'Dropoff Earliest',
      isDisableSort: true,
      renderer: (v) => (!v ? 'N/A' : moment(v).format('YYYY-MM-DD HH:mm')),
    },
    {
      fieldName: 'dropoff_latest_ts',
      label: 'Dropoff Latest',
      isDisableSort: true,
      renderer: (v) => (!v ? 'N/A' : moment(v).format('YYYY-MM-DD HH:mm')),
    },
    { fieldName: 'dropoff_note', label: 'Dropoff Note', isDisableSort: true },
    {
      fieldName: 'delivery_proof_photo_required',
      label: 'POD required',
      isDisableSort: true,
      renderer: (v) => !!v && 'Yes',
    },
    {
      fieldName: 'signature_required',
      label: 'Signature required',
      isDisableSort: true,
      renderer: (v) => !!v && 'Yes',
    },
    {
      fieldName: 'sms_enabled',
      label: 'SMS required',
      isDisableSort: true,
      renderer: (v) => !!v && 'Yes',
    },
    {
      fieldName: 'id_required',
      label: 'ID required',
      isDisableSort: true,
      renderer: (v) => !!v && 'Yes',
    },
    {
      fieldName: 'parcels',
      label: 'Parcels',
      isDisableSort: true,
      renderer: (v) => v?.length,
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box className={classes.jobInfo} sx={{ p: 1 }}>
        <Grid container spacing={1} className={classes.infoRow}>
          <Grid className={classes.label}>Job ID</Grid>
          <Grid sx={{ flexGrow: 1 }}>{jobDetail?.id}</Grid>
        </Grid>
        <Grid container spacing={1} className={classes.infoRow}>
          <Grid className={classes.label}>Uploader</Grid>
          <Grid sx={{ flexGrow: 1 }}>
            [{jobDetail?.client_id}] {jobDetail?.uploader}
          </Grid>
        </Grid>
        <Grid container spacing={1} className={classes.infoRow}>
          <Grid className={classes.label}>Action / Status</Grid>
          <Grid sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'inline-block' }}>{jobDetail?.action}</Box>
            <Box sx={{ display: 'inline-block', mx: 1 }}> - </Box>
            <Box
              sx={{
                display: 'inline-block',
                color: getStatusColor(jobDetail?.status),
                fontWeight: 700,
              }}
            >
              {jobDetail?.status}
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={1} className={classes.infoRow}>
          <Grid className={classes.label}>File Name</Grid>
          <Grid sx={{ flexGrow: 1 }}>{getFileName(jobDetail?.path)}</Grid>
        </Grid>
        <Grid container spacing={1} className={classes.infoRow}>
          <Grid className={classes.label}>Clients</Grid>
          <Grid sx={{ flexGrow: 1 }}>{jobDetail?.client_ids?.map((id) => get(find(activeClients, ['client_id', id]), 'name', id)).join(' | ')}</Grid>
        </Grid>
      </Box>
      <Box sx={{ my: 1 }}>
        <Grid container spacing={1} sx={{ alignItems: 'center' }}>
          <Grid>
            <Button onClick={goBack} startIcon={<ArrowBackIosNew />} style={{ fontSize: 16 }}>
              <span>BACK</span>
            </Button>
          </Grid>
          <Grid sx={{ flexGrow: 1 }}>
            <Box>
              <TextField
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && updateFilters({ q: keyword, page: DEFAULT_FILTERS.page })}
                fullWidth
                size="small"
                margin="dense"
                placeholder="Search by shipment ID, internal ID or tracking code"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment className={classes.searchIcon} position="start">
                        <SearchIcon fontSize="medium" />
                      </InputAdornment>
                    ),
                    endAdornment: keyword && (
                      <IconButton onClick={clearSearch} className={classes.closeBtn} size="medium">
                        <CloseIcon fontSize="medium" />
                      </IconButton>
                    ),
                  },
                }}
              />
            </Box>
          </Grid>
          <Grid>
            <Tooltip title={`Unique: ${jobDetail?.unique_count} / Duplicate: ${jobDetail?.duplicate_count}`}>
              <Box sx={{ fontSize: 24 }}>
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Total:{' '}
                </Box>
                <Box component="span" sx={{ color: 'secondary.main' }}>
                  {shipmentsResult?.total}
                </Box>
                <Box component="span" sx={{ ml: 0.5 }}>
                  <span>(</span>
                  <Box component="span" sx={{ color: 'green' }}>
                    {jobDetail?.unique_count}
                  </Box>
                  <span> / </span>
                  <Box component="span" sx={{ color: 'red' }}>
                    {jobDetail?.duplicate_count}
                  </Box>
                  <span>)</span>
                </Box>
              </Box>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ my: 1 }}>
        <AxlTableContainer fields={fields} result={shipmentsResult} filters={filters} isLoading={fetching} tableStyles={{ maxHeight: 'none' }} onPageChange={onPageChange} pagination={true} />
      </Box>
    </Box>
  );
}

export default SftpJobDetail;
